import { createFileRoute } from "@tanstack/react-router";
// Deep-import the pure param helpers instead of the `~/features/symbol` barrel
// (this eager route file must not statically pull the viem-tainted trade
// surface). The heavy graph still loads lazily via the dynamic import in the
// loader below.
import {
  normalizeSymbolRouteAssetClass,
  parseCanonicalSymbolRouteParams,
  type SymbolRouteAssetClass,
} from "~/features/symbol/symbol-route-params";
import { SymbolPagePending } from "~/features/symbol/routing/symbol-page-pending";
import { createSocialHeadMeta } from "~/lib/social-head";
import { createSymbolSocialMetadata } from "~/lib/social-metadata";

interface CanonicalSymbolSearch {
  class?: SymbolRouteAssetClass;
}

export const Route = createFileRoute("/_app/symbol/$symbol")({
  params: {
    priority: 0,
    parse: parseCanonicalSymbolRouteParams,
  },
  // Paint the page silhouette inside the live chrome the moment navigation
  // commits while the lazy chunk loads (see canonical-symbol-route-options).
  pendingComponent: SymbolPagePending,
  pendingMs: 0,
  validateSearch: (raw: Record<string, unknown>): CanonicalSymbolSearch => {
    const assetClass = normalizeSymbolRouteAssetClass(
      typeof raw.class === "string" ? raw.class : null,
    );
    return assetClass ? { class: assetClass } : {};
  },
  loaderDeps: ({ search }): { assetClass: SymbolRouteAssetClass | null } => ({
    assetClass: search.class ?? null,
  }),
  loader: ({ context, params, deps, preload }) => {
    // Never gate navigation on data: preload may wait for the code chunk only
    // (see canonical-symbol-route-options.ts for the full rationale).
    const moduleTask = import("~/features/symbol/symbol-route");
    moduleTask
      .then(({ preloadSymbolRoute }) =>
        preloadSymbolRoute(context, {
          ...params,
          assetClass: deps?.assetClass ?? null,
        }),
      )
      .catch(() => {
        // Warmup is best-effort; the page's own queries surface real errors.
      });

    if (preload) {
      return moduleTask.then(() => undefined);
    }
  },
  head: ({ params }) => {
    return {
      meta: [
        ...createSocialHeadMeta(createSymbolSocialMetadata(params.symbol)),
        { content: "noindex", name: "robots" },
      ],
    };
  },
});
